<template>
  <SideModal
    :show="!!district.district_id && show"
    @update:show="
      {
        if (!state.loadingSave) emit('update:show', $event);
      }
    "
    location="right"
    close-on-overlay-click
  >
    <!-- 
Add New School button which opens form -->
    <form @submit.prevent="save" class="modal-container">
      <div class="modal-top">
        <span class="d-flex gap-3 align-center">
          <Typography color="primary" weight="600" size="1.25rem">Edit District</Typography>
          <v-icon size="x-small" @click="copyToClipboard(district.district_id + '')"
            >mdi-content-copy</v-icon
          >
        </span>
        <v-btn
          @click="
            {
              if (!state.loadingSave) emit('update:show', false);
            }
          "
          variant="plain"
          color="normal"
          icon
          ><v-icon color="gray600">mdi-close</v-icon></v-btn
        >
      </div>

      <div class="modal-body">
        <v-text-field
          :disabled="district.deleted"
          class="custom-input"
          color="primary"
          label="District Name"
          v-model="district.district_name"
        ></v-text-field>
        <div class="table-overflow-hide" style="margin-top: 30px !important">
          <div class="directoryTable scrollable scrollable-55vh">
            <table class="table">
              <thead class="sticky-header">
                <tr>
                  <th>School Name</th>
                  <th>School ID</th>
                  <th class="icon-col">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in district.schools.filter((i) => !i.district && !i.deleted)"
                  :key="item.school_id"
                >
                  <td>{{ item.school_name }}</td>
                  <td>{{ item.school_id }}</td>
                  <td>
                    <v-btn
                      data-cy="directory-impersonate-btn"
                      @click="
                        {
                          impersonate(
                            item.username,
                            item.district_id,
                            item.school_id,
                            item.subdomain,
                            item.district
                          );
                        }
                      "
                      :loading="state.loadImpersonate.includes(item.school_id)"
                      color="gray300"
                      density="compact"
                      icon
                      variant="text"
                      :disabled="item.deleted"
                    >
                      <v-tooltip activator="parent" location="bottom"
                        >Log in as this school</v-tooltip
                      >
                      <v-icon color="gray500">mdi-security</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <CreateSchoolInDistrict :district="district"></CreateSchoolInDistrict>
      </div>
      <div class="modal-bottom">
        <div style="display: flex; width: 100%; gap: 15px; justify-content: stretch">
          <v-btn
            v-if="!district.deleted"
            @click="setDelete(true)"
            :loading="state.loadingDelete"
            style="text-transform: capitalize; flex-grow: 1"
            color="#E5E4DB"
          >
            Delete
          </v-btn>
          <v-btn
            v-if="!district.deleted"
            type="submit"
            :loading="state.loadingSave"
            style="text-transform: capitalize; flex-grow: 1"
            >Save</v-btn
          >
          <v-btn
            v-else
            @click="setDelete(false)"
            :loading="state.loadingDelete"
            style="text-transform: capitalize; flex-grow: 1"
            >Restore</v-btn
          >
        </div>
      </div>
    </form>
  </SideModal>
</template>

<script setup lang="ts">
import type { GetSupportDistrict } from "@/api_types/school";
import SideModal from "@/components/sideModal.vue";
import Typography from "@/components/typography.vue";

import { watch } from "vue";
import { reactive } from "vue";
import { useAlertsStore } from "@/stores/alerts";
import { cloneDeep } from "lodash";
import { useSchools } from "@/stores/support/schools";
// import FormValidate from "@/components/formValidate.vue";
import { copyToClipboard } from "@/utils";
import FetchService from "@/services/fetch.service";
import authService from "@/services/auth.service";
import CreateSchoolInDistrict from "./createSchoolInDistrict.vue";
import { computed } from "vue";

const schoolStore = useSchools();

const props = defineProps<{
  district_id: number;
  show: boolean;
}>();

const state = reactive({
  loadingSave: false,
  loadingDelete: false,
  school_name: "",
  district: {
    district_name: ""
  },
  loadImpersonate: [] as number[]
});

const syncedDistrict = computed(() =>
  schoolStore.districts.data.find((i) => i.district_id == props.district_id)
);

const district = reactive(cloneDeep(syncedDistrict.value || {
  district_id: props.district_id,
  district_name: "",
  schools: [],
  sif_id: null,
  deleted: false,
  created_tstamp: "",
  modified_tstamp: ""
} as GetSupportDistrict));

watch([syncedDistrict, () => props.show], () => {
  if (syncedDistrict.value) Object.assign(district, cloneDeep(syncedDistrict.value));
});

// Using array syntax instead of TypeScript generics for defineEmits
const emit = defineEmits(['update:show']);

const save = async () => {
  state.loadingSave = true;
  console.log("saving....");
  const res = await schoolStore.updateDistrict(district.district_id, {
    district_name: district.district_name
  });
  useAlertsStore().addAlert(
    res ? "success" : "error",
    res ? "District Details updated successfully" : "Failed to update district details",
    7
  );

  state.loadingSave = false;
};
const setDelete = async (val: boolean) => {
  state.loadingDelete = true;
  const message = val ? "delete" : "restore";
  let res;
  district.deleted = val;
  if (val) {
    res = await schoolStore.deleteDistrict(district.district_id);
  } else {
    res = await schoolStore.updateDistrict(district.district_id, { deleted: val });
  }

  useAlertsStore().addAlert(
    res ? "success" : "error",
    res ? `District ${message}d successfully` : `Failed to ${message} district`,
    7
  );

  state.loadingDelete = false;
};

const impersonate = async (username, district_id, school_id, account, is_district: boolean) => {
  state.loadImpersonate.push(school_id);
  let type = "district";
  if (is_district) {
    type = "district";
  }
  try {
    const res = await FetchService.fetchData(
      `/impersonate?uid=${username}&district_id=${district_id}&school_id=${school_id}&type=${type}`,
      "get"
    );
    if (!res) throw "Failed";
    const data = authService.parseJwt(res, false);
    const tokenName = `ptcwizard_${account}_school_jwt`;
    localStorage.setItem(tokenName, JSON.stringify(data));
    window.open(`/${account}/school`, "_blank");
  } catch (error) {
    useAlertsStore().addAlert("error", "Failed to impersonate school, please try again later");
  }
  state.loadImpersonate = state.loadImpersonate.filter((str) => str !== school_id);
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.modal-container {
  padding: 30px;
  display: flex;
  flex-direction: column;
  height: 100vh;

  .modal-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .modal-body {
    width: 100%;
  }
  .modal-body {
    align-self: flex-end;
    flex: 2;
  }
}

.scrollable {
  max-height: 45vh !important;
  height: auto !important;
}

.icon-col {
  width: 0px;
}
</style>
